import { useState, useRef, useEffect } from 'react'
import { Bell, Check, CheckCheck, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useNotificationStore } from '../../stores/notificationStore'
import { cn, severityBg, formatTimeAgo } from '../../lib/utils'

export default function NotificationCenter() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { notifications, markRead, markAllRead } = useNotificationStore()
  const unreadCount = useNotificationStore((s) => s.unreadCount())

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [])

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
        aria-expanded={open}
        className="relative p-2 rounded-lg hover:bg-bg-hover transition-colors text-text-secondary hover:text-text-primary"
      >
        <Bell size={20} aria-hidden="true" />
        {unreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] bg-accent text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div role="dialog" aria-label="Notifications" className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-bg-card border border-border rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-sm font-semibold">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-accent-blue hover:text-accent-blue-hover flex items-center gap-1"
                >
                  <CheckCheck size={14} />
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} aria-label="Close notifications" className="p-1 text-text-secondary hover:text-text-primary">
                <X size={16} aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-12 text-center text-text-secondary text-sm">
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => {
                    markRead(n.id)
                    if (n.deviceId) navigate(`/devices/${n.deviceId}`)
                    setOpen(false)
                  }}
                  className={cn(
                    'w-full text-left px-4 py-3 border-b border-border/50 hover:bg-bg-hover transition-colors',
                    !n.read && 'bg-bg-hover/50',
                  )}
                >
                  <div className="flex items-start gap-3">
                    <span className={cn('mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase', severityBg(n.severity))}>
                      {n.severity}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={cn('text-sm', n.read ? 'text-text-secondary' : 'text-text-primary font-medium')}>
                        {n.title}
                      </p>
                      <p className="text-xs text-text-muted mt-0.5 truncate">{n.message}</p>
                      <p className="text-xs text-text-muted mt-1">{formatTimeAgo(n.timestamp)}</p>
                    </div>
                    {!n.read && (
                      <span className="mt-1.5 w-2 h-2 rounded-full bg-accent shrink-0" />
                    )}
                  </div>
                </button>
              ))
            )}
          </div>

          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-border">
              <button
                onClick={() => { navigate('/alerts'); setOpen(false) }}
                className="text-xs text-accent-blue hover:text-accent-blue-hover flex items-center gap-1"
              >
                <Check size={14} />
                View all alerts
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
